<template>
  <v-container v-if="loadedSettings" fluid>
    <v-card outlined>
      <v-card-text>
        <v-container fluid>
          <v-card-title class="pl-0">{{ $t('settings.lockers') }}</v-card-title>
          <v-row>
            <v-col>
              <v-switch
                v-model="loadedSettings.lockerForPreparation"
                :disabled="loading"
                :label="$t('settings.activateLockerForPreparation')"
                persistent-hint :hint="$t('settings.activateLockerForPreparationMessage')"
              />
              <validation-provider
                v-if="loadedSettings.lockerForPreparation"
                rules="required"
                name="locker for preparation types"
                v-slot="{ errors }"
              >
                <v-select
                  v-model="loadedSettings.lockerForPreparationTypes"
                  :label="$t('settings.lockerForPreparationTypes')"
                  :error-messages="errors"
                  multiple
                  :items="preparationLockerTypes"
                ></v-select>
              </validation-provider>
            </v-col>
            <v-col>
              <v-switch
                v-model="loadedSettings.lockerForReception"
                :disabled="loading"
                :label="$t('settings.activateLockerForReception')"
                persistent-hint :hint="$t('settings.activateLockerForReceptionMessage')"
              />

              <validation-provider
                v-if="loadedSettings.lockerForReception"
                rules="required"
                name="locker for reception types"
                v-slot="{ errors }"
              >
                <v-select
                  v-model="loadedSettings.lockerForReceptionTypes"
                  :label="$t('settings.lockerForPreparationTypes')"
                  :error-messages="errors"
                  multiple
                  :items="receptionLockerTypes"
                ></v-select>
              </validation-provider>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-card-title class="pl-0">{{ $t('settings.boxes') }}</v-card-title>
              <v-switch
                v-model="loadedSettings.boxForPreparation"
                :disabled="loading"
                :label="$t('settings.boxesActivate')"
                persistent-hint :hint="$t('settings.boxesActivateMessage')"
              />
              <v-row v-if="loadedSettings.boxForPreparation" class="pl-10">
                <v-col cols="12" >
                  <validation-provider rules="required" name="box for preparation types" v-slot="{ errors }">
                    <v-select
                      v-model="loadedSettings.boxForPreparationTypes"
                      :label="$t('settings.boxForPreparationTypes')"
                      :error-messages="errors"
                      multiple
                      :items="preparationBoxTypes"
                    ></v-select>
                  </validation-provider>
                </v-col>
              </v-row>
            </v-col>
            <v-col class="pl-0 pr-0" v-show="loadedSettings.boxForPreparation">
              <box-settings-table
                :boxes="loadedSettings.preparation?.boxTypes || []"
                :loading="loading"
                @open-new="openNewBoxFormDialog"
                @edit="openModifyBoxFormDialog"
                @delete="openDeleteBoxDialog"
              />
              <box-form-dialog
                v-if="boxFormDialog"
                v-model="boxFormDialog"
                :formValue="selectedBox"
                @validateForm="saveBox"
                @close="boxFormDialog = false"
              />
              <yes-no-dialog
                v-model="deleteBoxDialog"
                :title="$t('settings.box.deleteBox')"
                :text="$t('settings.box.deleteBoxConfirmation')"
                @validate="deleteItem"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-card-title class="pl-0">{{ $t('settings.pickupCode') }}</v-card-title>
              <v-switch
                v-model="loadedSettings.pickupCodeActivate"
                :disabled="loading"
                :label="$t('settings.pickupCodeActivate')"
                persistent-hint :hint="$t('settings.pickupCodeActivateMessage')"
              />
              <v-row v-show="loadedSettings.pickupCodeActivate" class="pl-10">
                <v-col cols="12">
                  <v-select
                    v-model="loadedSettings.pickupCodeDisplayAllowedRoles"
                    :items="allDisplayRoles"
                    :label="$t('settings.pickupCodeDisplayAllowedRoles')"
                    multiple
                  ></v-select>
                </v-col>
              </v-row>
              <v-row v-show="loadedSettings.pickupCodeActivate" class="pl-10">
                <v-col cols="12">
                  <v-select
                    v-model="loadedSettings.pickupCodeResendAllowedRoles"
                    :items="roles"
                    :label="$t('settings.pickupCodeResendAllowedRoles')"
                    multiple
                  ></v-select>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-card-title class="pl-0">{{ $t('settings.pickPackShip') }}</v-card-title>
              <v-switch v-model="loadedSettings.activatePickPackShip" :label="$t('settings.activatePickPackShip')" />
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
  import { ValidationProvider, extend } from 'vee-validate'
  import { required } from 'vee-validate/dist/rules'
  import boxSettingsTable from './BoxSettingsTable.vue'
  import boxFormDialog from '../dialogs/BoxFormDialog.vue'
  import YesNoDialog from '../dialogs/YesNoDialog.vue'

  extend("required", { ...required })
  export default {
    name: 'PreparationSettings',
    components: {
      boxSettingsTable,
      boxFormDialog,
      YesNoDialog,
      "validation-provider": ValidationProvider
    },
    data() {
      return {
        preparationLockerTypes: [
          { text: this.$t('subOrder.clickAndReserve'), value: 'click_and_reserve' },
          { text: this.$t('subOrder.clickAndCollect'), value: 'click_and_collect' },
          { text: this.$t('subOrder.shipFromStore'), value: 'ship_from_store' },
          { text: this.$t('subOrder.interStore'), value: 'inter_store' },
        ],
        receptionLockerTypes: [
          { text: this.$t('subOrder.inStorePickup'), value: 'in_store_pickup' },
          { text: this.$t('subOrder.interStore'), value: 'inter_store' },
        ],
        preparationBoxTypes: [
          { text: this.$t('subOrder.clickAndReserve'), value: 'click_and_reserve' },
          { text: this.$t('subOrder.clickAndCollect'), value: 'click_and_collect' },
          { text: this.$t('subOrder.shipFromStore'), value: 'ship_from_store' },
          { text: this.$t('subOrder.interStore'), value: 'inter_store' },
        ],
        selectedBox: undefined,
        editingBox: false,
        deleteBoxDialog: false,
        boxFormDialog: false
      }
    },
    props: {
      loadedSettings: {
        type: Object,
        required: true,
      },
      loading: {
        type: Boolean,
        default: false,
      },
    },
    methods: {
      validateForm() {
        return this.$refs.form.validate();
      },
      openNewBoxFormDialog() {
        this.selectedBox = undefined
        this.editingBox = false
        this.boxFormDialog = true
      },
      saveBox(box) {
        if (!this.loadedSettings.preparation) {
          this.$set(this.loadedSettings, 'preparation', { boxTypes: [] })
        }
        const currentBoxes = this.loadedSettings.preparation.boxTypes || []

        if (this.editingBox) {
          // Update existing box
          const index = currentBoxes.indexOf(this.selectedBox)
          if (index !== -1) {
            box.id = this.selectedBox.id
            currentBoxes[index] = box
            this.$set(this.loadedSettings.preparation, 'boxTypes', [...currentBoxes])
          }
        } else {
          // Add new box
          this.$set(this.loadedSettings.preparation, 'boxTypes', [box, ...currentBoxes])
        }

        this.boxFormDialog = false
        this.selectedBox = undefined
      },
      openModifyBoxFormDialog(item) {
        this.selectedBox = item
        this.editingBox = true
        this.boxFormDialog = true
      },
      openDeleteBoxDialog(item) {
        this.selectedBox = item
        this.deleteBoxDialog = true
      },
      deleteItem(){
        const boxTypes = this.loadedSettings.preparation?.boxTypes || []
        const index = boxTypes.indexOf(this.selectedBox)
        if (index !== -1) {
          boxTypes.splice(index, 1)
        }
        this.$set(this.loadedSettings.preparation, 'boxTypes', [...boxTypes])
        this.deleteBoxDialog = false
        this.selectedBox = {}
      },
    },
    computed: {
      allDisplayRoles() {
        return [
          { text: this.$t('users.customerService'), value: 'oms-customer-service' },
          { text: this.$t('users.admin'), value: 'oms-admin' },
        ]
      },
      roles() {
        return [
          { text: this.$t('users.seller'), value: 'default-user' },
          { text: this.$t('users.shopManager'), value: 'oms-shop-manager' },
          { text: this.$t('users.customerService'), value: 'oms-customer-service' },
          { text: this.$t('users.admin'), value: 'oms-admin' },
        ]
      },
    },
  }
</script>
